/**
 * ERPLayout — Master Design System for Las Dos Doncellas ERP
 * Single source of truth for all visual components.
 * All pages MUST use these components — no exceptions.
 *
 * DESIGN TOKENS (Executive Dashboard standard):
 *   Background:  #111111
 *   Cards:       #1A1A1A
 *   Borders:     rgba(255,255,255,0.08)
 *   Text:        #FFFFFF
 *   Text muted:  #A0A0A0
 *   Yellow:      #D6EF4E  (titles, highlights)
 *   Green:       #3A7F6B  (positive values)
 *   Red:         #E05252  (costs, negative)
 *   Gold:        #C9A24D  (secondary metrics)
 *   Blue:        #8097D6  (info)
 *   Font:        Oranienbaum (ALL elements)
 */

import React, { useState } from 'react';
import Icon from '../AppIcon';

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
export const ERP_COLORS = {
  bgMain: '#111111',
  bgCard: '#1A1A1A',
  bgCardHover: '#202020',
  bgHeader: '#141414',
  border: 'rgba(255,255,255,0.08)',
  borderHover: 'rgba(255,255,255,0.15)',
  yellow: '#D6EF4E',
  green: '#3A7F6B',
  greenLight: '#4CAF85',
  red: '#E05252',
  blue: '#8097D6',
  gold: '#C9A24D',
  textPrimary: '#FFFFFF',
  textSecondary: 'rgba(255,255,255,0.6)',
  textMuted: 'rgba(255,255,255,0.35)',
};

// ─── PAGE WRAPPER ─────────────────────────────────────────────────────────────
export const ERPPage = ({ children, className = '' }) => (
  <div className={`min-h-screen bg-[#111111] px-5 lg:px-7 py-6 ${className}`}>
    {children}
  </div>
);

// Alias for semantic clarity
export const ERP_MASTER_LAYOUT = ERPPage;

// ─── PAGE HEADER ──────────────────────────────────────────────────────────────
export const ERPPageHeader = ({ title, subtitle, actions }) => (
  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
    <div>
      <h1 className="font-oranienbaum font-bold text-[#D6EF4E] leading-tight erp-page-title">
        {title}
      </h1>
      {subtitle && (
        <p className="font-oranienbaum text-white/55 mt-1 erp-page-subtitle">{subtitle}</p>
      )}
    </div>
    {actions && (
      <div className="flex items-center gap-3 flex-shrink-0 flex-wrap">
        {actions}
      </div>
    )}
  </div>
);

// ─── ACTION BAR / FILTER BAR ──────────────────────────────────────────────────
export const ERPActionBar = ({ children, className = '' }) => (
  <div className={`flex flex-wrap items-center gap-3 mb-6 p-4 bg-[#1A1A1A] rounded-xl border border-white/8 ${className}`}>
    {children}
  </div>
);

// Alias for semantic clarity
export const ERP_FILTER_BAR = ERPActionBar;

// ─── KPI GRID ─────────────────────────────────────────────────────────────────
export const ERPKPIGrid = ({ children, cols = 4 }) => {
  const colClass = cols === 3 ? 'lg:grid-cols-3' : cols === 5 ? 'xl:grid-cols-5 lg:grid-cols-4' : cols === 6 ? 'xl:grid-cols-5 lg:grid-cols-4' : 'lg:grid-cols-4';
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 ${colClass} gap-4 mb-6`}>
      {children}
    </div>
  );
};

// ─── KPI CARD ─────────────────────────────────────────────────────────────────
export const ERPKPICard = ({ title, value, subtitle, icon, iconColor, valueColor, loading, trend, trendLabel }) => (
  <div className="bg-[#1A1A1A] rounded-xl border border-white/8 shadow-sm hover:border-white/15 transition-all duration-200 flex flex-col justify-between"
    style={{ padding: 'var(--card-padding)', minHeight: 'var(--card-min-height)' }}>
    <div className="flex items-start justify-between mb-3">
      <span className="font-oranienbaum pr-2 font-600 erp-kpi-label text-white/80">{title}</span>
      {icon && (
        <div
          className="rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ width: '2.5rem', height: '2.5rem', background: `${iconColor || '#D6EF4E'}18` }}
        >
          <Icon name={icon} size={22} style={{ color: iconColor || '#D6EF4E' }} />
        </div>
      )}
    </div>
    {loading ? (
      <div className="h-9 w-24 bg-white/5 rounded animate-pulse" />
    ) : (
      <p className="font-bold font-oranienbaum erp-kpi-value" style={{ color: valueColor || '#ffffff' }}>
        {value ?? '—'}
      </p>
    )}
    {(subtitle || trend !== undefined) && !loading && (
      <div className="flex items-center justify-between mt-2">
        {subtitle && <p className="font-oranienbaum text-white/45 truncate erp-kpi-desc">{subtitle}</p>}
        {trend !== undefined && (
          <span className={`font-oranienbaum font-semibold erp-kpi-desc ${trend >= 0 ? 'text-[#3A7F6B]' : 'text-[#E05252]'}`}>
            {trend >= 0 ? '▲' : '▼'} {Math.abs(trend)?.toFixed(1)}%
          </span>
        )}
      </div>
    )}
  </div>
);

// Alias
export const ERP_KPI_CARD = ERPKPICard;

// ─── CARD ─────────────────────────────────────────────────────────────────────
export const ERPCard = ({ children, className = '', title, titleAction }) => (
  <div className={`bg-[#1A1A1A] rounded-xl border border-white/8 shadow-sm ${className}`}>
    {title && (
      <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-white/5">
        <h2 className="font-bold font-oranienbaum text-[#D6EF4E] erp-section-title">{title}</h2>
        {titleAction}
      </div>
    )}
    <div style={{ padding: 'var(--card-padding)' }}>
      {children}
    </div>
  </div>
);

// ─── SECTION DIVIDER ──────────────────────────────────────────────────────────
export const ERPSectionDivider = ({ label }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className="h-px flex-1 bg-white/8" />
    {label && <span className="font-oranienbaum font-semibold text-white/30 uppercase tracking-widest whitespace-nowrap erp-badge">{label}</span>}
    <div className="h-px flex-1 bg-white/8" />
  </div>
);

// ─── TABLE WRAPPER ────────────────────────────────────────────────────────────
export const ERPTable = ({ children, className = '' }) => (
  <div className={`w-full overflow-x-auto rounded-xl border border-white/8 bg-[#1A1A1A] ${className}`}>
    <table className="w-full font-oranienbaum" style={{ fontSize: 'var(--fs-table-body)' }}>
      {children}
    </table>
  </div>
);

export const ERPTableHead = ({ children }) => (
  <thead>
    <tr className="border-b border-white/8 bg-[#141414]" style={{ height: 'var(--table-header-height)' }}>
      {children}
    </tr>
  </thead>
);

export const ERPTableTh = ({ children, className = '', onClick, sortable }) => (
  <th
    className={`text-left font-semibold font-oranienbaum text-white/55 uppercase tracking-wider whitespace-nowrap erp-table-header ${sortable ? 'cursor-pointer hover:text-white/80 select-none' : ''} ${className}`}
    style={{ paddingLeft: 'var(--table-cell-px)', paddingRight: 'var(--table-cell-px)', paddingTop: 'var(--table-cell-py)', paddingBottom: 'var(--table-cell-py)' }}
    onClick={onClick}
  >
    <div className="flex items-center gap-1.5">
      {children}
      {sortable && <Icon name="ChevronsUpDown" size={14} className="text-white/30" />}
    </div>
  </th>
);

export const ERPTableBody = ({ children }) => (
  <tbody className="divide-y divide-white/5">
    {children}
  </tbody>
);

export const ERPTableRow = ({ children, onClick, selected, className = '' }) => (
  <tr
    className={`transition-colors duration-100 ${onClick ? 'cursor-pointer' : ''} ${selected ? 'bg-[#D6EF4E]/8 border-l-2 border-l-[#D6EF4E]' : 'hover:bg-white/3'} ${className}`}
    style={{ minHeight: 'var(--table-row-height)' }}
    onClick={onClick}
  >
    {children}
  </tr>
);

export const ERPTableTd = ({ children, className = '' }) => (
  <td className={`font-oranienbaum text-white/80 erp-table-body ${className}`}
    style={{ paddingLeft: 'var(--table-cell-px)', paddingRight: 'var(--table-cell-px)', paddingTop: 'var(--table-cell-py)', paddingBottom: 'var(--table-cell-py)' }}>
    {children}
  </td>
);

// ─── ERP DATA TABLE (full reusable table with pagination) ─────────────────────
export const ERP_DATA_TABLE = ({
  columns,
  data,
  loading,
  onRowClick,
  selectedId,
  getRowId = (row) => row?.id,
  emptyMessage = 'No se encontraron registros',
  // Pagination props
  currentPage,
  totalPages,
  totalRecords,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100, 200],
  showPagination = true,
}) => (
  <div className="w-full overflow-x-auto rounded-xl border border-white/8 bg-[#1A1A1A]">
    <table className="w-full font-oranienbaum" style={{ fontSize: 'var(--fs-table-body)' }}>
      <thead>
        <tr className="border-b border-white/8 bg-[#141414]" style={{ height: 'var(--table-header-height)' }}>
          {columns?.map((col) => (
            <th
              key={col?.key}
              className={`text-left font-semibold font-oranienbaum text-white/55 uppercase tracking-wider whitespace-nowrap erp-table-header ${col?.sortable ? 'cursor-pointer hover:text-white/80 select-none' : ''} ${col?.className || ''}`}
              style={{ paddingLeft: 'var(--table-cell-px)', paddingRight: 'var(--table-cell-px)', paddingTop: 'var(--table-cell-py)', paddingBottom: 'var(--table-cell-py)' }}
              onClick={col?.onSort}
            >
              <div className="flex items-center gap-1.5">
                {col?.label}
                {col?.sortable && <Icon name="ChevronsUpDown" size={14} className="text-white/30" />}
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-white/5">
        {loading ? (
          <tr>
            <td colSpan={columns?.length} className="px-5 py-16 text-center">
              <div className="flex flex-col items-center gap-3">
                <Icon name="Loader2" size={32} className="text-[#D6EF4E] animate-spin" />
                <span className="font-oranienbaum text-white/40 erp-secondary-text">Cargando datos...</span>
              </div>
            </td>
          </tr>
        ) : !data?.length ? (
          <tr>
            <td colSpan={columns?.length} className="px-5 py-16 text-center">
              <div className="flex flex-col items-center gap-3">
                <Icon name="Inbox" size={44} className="text-white/15" />
                <span className="font-oranienbaum text-white/35 erp-secondary-text">{emptyMessage}</span>
              </div>
            </td>
          </tr>
        ) : (
          data?.map((row) => {
            const rowId = getRowId(row);
            const isSelected = selectedId !== undefined && rowId === selectedId;
            return (
              <tr
                key={rowId}
                className={`transition-colors duration-100 ${onRowClick ? 'cursor-pointer' : ''} ${isSelected ? 'bg-[#D6EF4E]/8 border-l-2 border-l-[#D6EF4E]' : 'hover:bg-white/3'}`}
                style={{ minHeight: 'var(--table-row-height)' }}
                onClick={() => onRowClick?.(row)}
              >
                {columns?.map((col) => (
                  <td key={col?.key} className={`font-oranienbaum text-white/80 erp-table-body ${col?.tdClassName || ''}`}
                    style={{ paddingLeft: 'var(--table-cell-px)', paddingRight: 'var(--table-cell-px)', paddingTop: 'var(--table-cell-py)', paddingBottom: 'var(--table-cell-py)' }}>
                    {col?.render ? col?.render(row) : row?.[col?.key]}
                  </td>
                ))}
              </tr>
            );
          })
        )}
      </tbody>
    </table>
    {showPagination && totalRecords > 0 && (
      <ERPPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        pageSizeOptions={pageSizeOptions}
      />
    )}
  </div>
);

// ─── PAGINATION ───────────────────────────────────────────────────────────────
export const ERPPagination = ({
  currentPage,
  totalPages,
  totalRecords,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100, 200],
}) => {
  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, totalRecords);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-5 py-4 border-t border-white/8 bg-[#141414] rounded-b-xl">
      {/* Records info + page size */}
      <div className="flex items-center gap-4">
        <span className="font-oranienbaum text-white/50 erp-secondary-text">
          Mostrando{' '}
          <span className="text-white/80 font-semibold">{startRecord}–{endRecord}</span>
          {' '}de{' '}
          <span className="text-white/80 font-semibold">{totalRecords}</span>
          {' '}registros
        </span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange?.(Number(e?.target?.value))}
          className="bg-[#1A1A1A] border border-white/10 text-white/75 rounded-lg px-3 font-oranienbaum focus:outline-none focus:border-[#D6EF4E]/40"
          style={{ fontSize: 'var(--fs-sm)', minHeight: '2.5rem' }}
        >
          {pageSizeOptions?.map((s) => (
            <option key={s} value={s}>{s} por página</option>
          ))}
        </select>
      </div>
      {/* Page controls */}
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => onPageChange?.(1)}
          disabled={currentPage === 1}
          className="px-3 py-2 rounded-lg font-oranienbaum text-white/55 hover:text-white hover:bg-white/8 disabled:opacity-30 disabled:cursor-not-allowed transition-colors erp-secondary-text"
          title="Primera página"
        >
          «
        </button>
        <button
          onClick={() => onPageChange?.(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-2 rounded-lg font-oranienbaum text-white/55 hover:text-white hover:bg-white/8 disabled:opacity-30 disabled:cursor-not-allowed transition-colors erp-secondary-text"
        >
          ‹ Anterior
        </button>
        <span className="px-4 py-2 font-oranienbaum text-white/75 bg-white/5 rounded-lg border border-white/8 erp-secondary-text">
          Página <span className="text-[#D6EF4E] font-bold">{currentPage}</span> de{' '}
          <span className="font-bold">{totalPages || 1}</span>
        </span>
        <button
          onClick={() => onPageChange?.(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="px-3 py-2 rounded-lg font-oranienbaum text-white/55 hover:text-white hover:bg-white/8 disabled:opacity-30 disabled:cursor-not-allowed transition-colors erp-secondary-text"
        >
          Siguiente ›
        </button>
        <button
          onClick={() => onPageChange?.(totalPages)}
          disabled={currentPage >= totalPages}
          className="px-3 py-2 rounded-lg font-oranienbaum text-white/55 hover:text-white hover:bg-white/8 disabled:opacity-30 disabled:cursor-not-allowed transition-colors erp-secondary-text"
          title="Última página"
        >
          »
        </button>
      </div>
    </div>
  );
};

// ─── BUTTON ───────────────────────────────────────────────────────────────────
export const ERPButton = ({
  children,
  variant = 'primary',
  size = 'md',
  icon,
  onClick,
  disabled,
  loading: btnLoading,
  className = '',
  type = 'button',
}) => {
  const base = 'inline-flex items-center gap-2 font-oranienbaum font-semibold rounded-lg transition-all duration-150 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed';
  const sizes = {
    sm:  'px-4 py-2.5 erp-secondary-text',
    md:  'px-5 py-3 erp-filter-label',
    lg:  'px-6 py-3.5 erp-base',
  };
  const variants = {
    primary:   'bg-[#3A7F6B] hover:bg-[#4CAF85] text-white shadow-sm',
    secondary: 'bg-[#1A1A1A] hover:bg-[#222] text-white/85 border border-white/10 hover:border-white/20',
    danger:    'bg-[#E05252]/15 hover:bg-[#E05252]/25 text-[#E05252] border border-[#E05252]/20',
    ghost:     'text-white/65 hover:text-white hover:bg-white/6',
    yellow:    'bg-[#D6EF4E] hover:bg-[#c8e040] text-black',
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || btnLoading}
      className={`${base} ${sizes?.[size] || sizes?.md} ${variants?.[variant] || variants?.primary} ${className}`}
      style={{ minHeight: size === 'sm' ? 'var(--control-height-sm)' : size === 'lg' ? 'var(--control-height-lg)' : 'var(--control-height-md)' }}
    >
      {btnLoading ? (
        <Icon name="Loader2" size={18} className="animate-spin" />
      ) : icon ? (
        <Icon name={icon} size={18} />
      ) : null}
      {children}
    </button>
  );
};

// ─── INPUT ────────────────────────────────────────────────────────────────────
export const ERPInput = ({ label, error, className = '', ...props }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    {label && <label className="font-oranienbaum text-white/60 erp-filter-label font-medium">{label}</label>}
    <input
      {...props}
      className={`bg-[#111111] border ${error ? 'border-[#E05252]/50' : 'border-white/10'} text-white/90 rounded-lg px-4 font-oranienbaum placeholder-white/30 focus:outline-none focus:border-[#D6EF4E]/40 transition-colors w-full erp-table-body`}
      style={{ minHeight: 'var(--control-height-md)', paddingTop: '0.75rem', paddingBottom: '0.75rem' }}
    />
    {error && <span className="font-oranienbaum text-[#E05252] erp-badge">{error}</span>}
  </div>
);

// ─── SELECT ───────────────────────────────────────────────────────────────────
export const ERPSelect = ({ label, error, className = '', children, ...props }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    {label && <label className="font-oranienbaum text-white/60 erp-filter-label font-medium">{label}</label>}
    <select
      {...props}
      className={`bg-[#111111] border ${error ? 'border-[#E05252]/50' : 'border-white/10'} text-white/90 rounded-lg px-4 font-oranienbaum focus:outline-none focus:border-[#D6EF4E]/40 transition-colors w-full erp-table-body`}
      style={{ minHeight: 'var(--control-height-md)', paddingTop: '0.75rem', paddingBottom: '0.75rem' }}
    >
      {children}
    </select>
    {error && <span className="font-oranienbaum text-[#E05252] erp-badge">{error}</span>}
  </div>
);

// ─── BADGE ────────────────────────────────────────────────────────────────────
export const ERPBadge = ({ children, color = 'default', className = '' }) => {
  const colors = {
    default: 'bg-white/8 text-white/65',
    green:   'bg-[#3A7F6B]/15 text-[#3A7F6B]',
    red:     'bg-[#E05252]/15 text-[#E05252]',
    yellow:  'bg-[#D6EF4E]/15 text-[#D6EF4E]',
    gold:    'bg-[#C9A24D]/15 text-[#C9A24D]',
    blue:    'bg-[#8097D6]/15 text-[#8097D6]',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full font-oranienbaum font-semibold erp-badge ${colors?.[color] || colors?.default} ${className}`}>
      {children}
    </span>
  );
};

// ─── LOADING STATE ────────────────────────────────────────────────────────────
export const ERPLoading = ({ message = 'Cargando datos...' }) => (
  <div className="flex flex-col items-center justify-center py-16 gap-4">
    <Icon name="Loader2" size={36} className="text-[#D6EF4E] animate-spin" />
    <p className="font-oranienbaum text-white/45 erp-secondary-text">{message}</p>
  </div>
);

// ─── ERROR STATE ──────────────────────────────────────────────────────────────
export const ERPError = ({ message, onRetry }) => (
  <div className="bg-[#E05252]/8 border border-[#E05252]/20 rounded-xl p-5 flex items-start gap-3">
    <Icon name="AlertCircle" size={20} className="text-[#E05252] flex-shrink-0 mt-0.5" />
    <div className="flex-1">
      <p className="font-oranienbaum text-[#E05252] erp-table-body">{message}</p>
      {onRetry && (
        <button onClick={onRetry} className="mt-2 font-oranienbaum text-white/55 hover:text-white underline erp-secondary-text">
          Reintentar
        </button>
      )}
    </div>
  </div>
);

// ─── EMPTY STATE ──────────────────────────────────────────────────────────────
export const ERPEmpty = ({ message = 'No se encontraron registros', icon = 'Inbox' }) => (
  <div className="flex flex-col items-center justify-center py-16 gap-4">
    <Icon name={icon} size={48} className="text-white/15" />
    <p className="font-oranienbaum text-white/35 erp-secondary-text">{message}</p>
  </div>
);

// ─── STAT ROW (for summary panels) ───────────────────────────────────────────
export const ERPStatRow = ({ label, value, valueColor, borderBottom = true }) => (
  <div className={`flex items-center justify-between py-2.5 ${borderBottom ? 'border-b border-white/5' : ''}`}>
    <span className="font-oranienbaum text-white/60 erp-secondary-text">{label}</span>
    <span className="font-bold font-oranienbaum erp-table-body" style={{ color: valueColor || '#ffffff' }}>{value}</span>
  </div>
);

// ─── SEARCH INPUT ─────────────────────────────────────────────────────────────
export const ERPSearchInput = ({ value, onChange, placeholder = 'Buscar...', className = '' }) => (
  <div className={`relative flex-1 min-w-[200px] ${className}`}>
    <Icon name="Search" size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/35 pointer-events-none" />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange?.(e?.target?.value)}
      placeholder={placeholder}
      className="w-full bg-[#111111] border border-white/10 text-white/90 rounded-lg font-oranienbaum placeholder-white/30 focus:outline-none focus:border-[#D6EF4E]/40 transition-colors erp-table-body"
      style={{ paddingLeft: '2.5rem', paddingRight: '1rem', minHeight: 'var(--control-height-md)', paddingTop: '0.75rem', paddingBottom: '0.75rem' }}
    />
  </div>
);

// ─── FILTER SELECT ────────────────────────────────────────────────────────────
export const ERPFilterSelect = ({ value, onChange, children, className = '' }) => (
  <select
    value={value}
    onChange={(e) => onChange?.(e?.target?.value)}
    className={`bg-[#111111] border border-white/10 text-white/75 rounded-lg px-4 font-oranienbaum focus:outline-none focus:border-[#D6EF4E]/40 transition-colors erp-filter-label ${className}`}
    style={{ minHeight: 'var(--control-height-md)', paddingTop: '0.75rem', paddingBottom: '0.75rem' }}
  >
    {children}
  </select>
);

// ─── CHART CARD ───────────────────────────────────────────────────────────────
export const ERPChartCard = ({ title, children, className = '', titleAction }) => (
  <div className={`bg-[#1A1A1A] rounded-xl border border-white/8 shadow-sm ${className}`}>
    <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-white/5">
      <h2 className="font-bold font-oranienbaum text-[#D6EF4E] erp-section-title">{title}</h2>
      {titleAction}
    </div>
    <div style={{ padding: 'var(--card-padding)' }}>
      {children}
    </div>
  </div>
);

export default ERPPage;
